import React, { useCallback, useEffect, useRef, useState } from "react";
import { Pressable, StyleSheet, View } from "react-native";
import { LocalizedText as Text } from "../i18n/ExhibitLocaleContext";

import { askAIExplained, logAIExplainedQuestionEngagement } from "../../api/aiExhibitAskClient";
import { tx } from "../i18n/exhibitLocale";

const QUESTIONS = [
  {
    id: "learn",
    icon: "🌱",
    q: { el: "How does a machine learn?", ms: "How does a machine learn?", hs: "How does a machine learn?" },
  },
  {
    id: "think",
    icon: "🧠",
    q: { el: "Can it think like a person?", ms: "Can it think like a person?", hs: "Can it think like a person?" },
  },
  {
    id: "hallucinate",
    icon: "🤥",
    q: { el: "Why does it sometimes make things up?", ms: "Why does it sometimes make things up?", hs: "Why does it sometimes make things up?" },
  },
  {
    id: "search",
    icon: "🔍",
    q: { el: "How is it different from a search engine?", ms: "How is it different from search?", hs: "How is it different from search?" },
  },
  {
    id: "conscious",
    icon: "👻",
    q: { el: "Does it have feelings?", ms: "Is anything conscious in there?", hs: "Is anything conscious in there?" },
  },
  {
    id: "jobs",
    icon: "💼",
    q: { el: "Could it change the jobs people do?", ms: "Could it change my job?", hs: "Could it change my job?" },
  },
  {
    id: "creative",
    icon: "🎨",
    q: { el: "Is what it makes really creative?", ms: "Is what it makes really creative?", hs: "Is what it makes really creative?" },
  },
  {
    id: "energy",
    icon: "⚡",
    q: { el: "How much electricity does it use?", ms: "How much energy does it use?", hs: "How much energy does it use?" },
  },
];

function t(value, tier) {
  if (typeof value === "string") return value;
  if (tier === "el") return value.el ?? value.ms ?? value.hs;
  if (tier === "ms") return value.ms ?? value.hs ?? value.el;
  return value.hs ?? value.ms ?? value.el;
}

export default function AskSarasScreen({
  tier = "hs",
  language = "en",
  onBack = () => {},
  visitorUserId,
  clientSessionId,
  exhibitVersion = "2.0",
  conversationStarted = false,
  onConversationStarted = () => {},
  onQuestionAsked = () => {},
  audioEnabled = false,
  onBeforeQuestionAudio = () => {},
}) {
  const abortRef = useRef(null);
  const audioRef = useRef(null);
  const audioQueueRef = useRef([]);
  const audioPlayingRef = useRef(false);
  const audioEnabledRef = useRef(false);

  const [selectedId, setSelectedId] = useState(null);
  const [lastQuestion, setLastQuestion] = useState("");
  const [answer, setAnswer] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");

  const stopAudio = useCallback(() => {
    audioQueueRef.current = [];
    audioPlayingRef.current = false;

    if (audioRef.current) {
      try {
        audioRef.current.pause();
        audioRef.current.src = "";
      } catch {}
    }
  }, []);

  const playNextAudio = useCallback(() => {
    if (audioPlayingRef.current || typeof Audio === "undefined") return;

    const nextUrl = audioQueueRef.current.shift();
    if (!nextUrl) return;

    if (!audioRef.current) audioRef.current = new Audio();

    audioPlayingRef.current = true;
    audioRef.current.src = nextUrl;
    audioRef.current.onended = () => {
      audioPlayingRef.current = false;
      playNextAudio();
    };
    audioRef.current.onerror = () => {
      audioPlayingRef.current = false;
      playNextAudio();
    };
    audioRef.current.play().catch(() => {
      audioPlayingRef.current = false;
      playNextAudio();
    });
  }, []);

  const enqueueAudio = useCallback(
    (url) => {
      if (!url || !audioEnabledRef.current) return;
      audioQueueRef.current.push(url);
      playNextAudio();
    },
    [playNextAudio]
  );

  useEffect(() => {
    audioEnabledRef.current = audioEnabled;
    if (!audioEnabled) stopAudio();
  }, [audioEnabled, stopAudio]);

  useEffect(() => {
    stopAudio();
  }, [language, stopAudio]);

  useEffect(
    () => () => {
      try {
        abortRef.current?.abort();
      } catch {}
      stopAudio();
    },
    [stopAudio]
  );

  const ask = async (item) => {
    if (!item || busy) return;

    try {
      abortRef.current?.abort();
    } catch {}

    const controller = new AbortController();
    abortRef.current = controller;

    const question = tx(language, t(item.q, tier));
    let liveText = "";

    onQuestionAsked(item);
    onBeforeQuestionAudio();

    setSelectedId(item.id);
    setLastQuestion(question);
    setAnswer("");
    setError("");
    setBusy(true);
    stopAudio();

    try {
      const response = await askAIExplained({
        userId: visitorUserId,
        clientSessionId,
        question,
        language,
        newDialog: !conversationStarted,
        curatedQuestionId: item.id,
        questionSource: "curated",
        exhibitVersion,
        audienceTier: tier,
        speakerMuted: !audioEnabled,
        signal: controller.signal,
        onEvent: (event) => {
          if (event?.type === "text_delta") {
            liveText += event.delta || "";
            setAnswer(liveText);
          } else if (event?.type === "text_done") {
            const finalText = event.text || liveText;
            if (finalText) setAnswer(finalText);
          } else if (event?.type === "audio_chunk" && event.url) {
            enqueueAudio(event.url);
          }
        },
      });

      const finalText =
        response?.["AI response"] ||
        response?.["Formatted AI response"] ||
        liveText;

      if (!String(finalText || "").trim()) {
        throw new Error(tx(language, "Saras AI returned no usable text."));
      }

      setAnswer(finalText);
      onConversationStarted();

      // Preserve the existing Saras engagement metric as well as the richer
      // AI Exhibit session/event analytics. Failure here must never block the answer.
      logAIExplainedQuestionEngagement({ userId: visitorUserId }).catch((engagementError) => {
        console.warn(
          "[AI EXPLAINED V2] Could not update existing ai_questions engagement metric:",
          engagementError?.message || engagementError
        );
      });
    } catch (err) {
      if (err?.name === "AbortError") return;
      console.error("[AI EXPLAINED V2] Ask Saras failed:", err);
      setError(tx(language, err?.message || "Saras AI could not answer right now."));
    } finally {
      if (abortRef.current === controller) abortRef.current = null;
      setBusy(false);
    }
  };

  const suggestions = QUESTIONS.filter((item) => item.id !== selectedId).slice(0, 3);

  return (
    <View style={s.container}>
      <View style={s.top}>
        <Pressable onPress={() => { stopAudio(); onBack(); }}>
          <Text style={s.topText}>{tx(language, "← Menu")}</Text>
        </Pressable>
        <Text style={s.topText}>{tx(language, "Ask about AI")}</Text>
      </View>

      {!selectedId ? (
        <View style={s.center}>
          <Text style={s.title}>{tx(language, "Pick a question.")}</Text>
          <Text style={s.subtitle}>
            {tx(language, t({
              el: "Saras AI will answer in plain words for this age group. No typing here, on purpose.",
              ms: "Saras AI will answer at a middle-school level, with clear explanations and useful AI terms.",
              hs: "Saras AI will answer for high-school and adult visitors, with a little more technical depth and nuance.",
            }, tier))}
          </Text>

          <View style={s.grid}>
            {QUESTIONS.map((item) => (
              <Pressable
                key={item.id}
                onPress={() => ask(item)}
                style={({ pressed }) => [s.question, pressed && s.pressed]}
              >
                <Text style={s.icon}>{item.icon}</Text>
                <Text style={s.questionText}>{tx(language, t(item.q, tier))}</Text>
              </Pressable>
            ))}
          </View>

          <Text style={s.foot}>
            No keyboard by design · answers are generated live, not scripted
          </Text>
        </View>
      ) : (
        <View style={s.answerLayout}>
          <Text style={s.answerKicker}>{tx(language, "YOU ASKED")}</Text>
          <Text style={s.answerQuestion}>{lastQuestion}</Text>

          <View style={s.answerCard}>
            <Text style={s.saras}>SARAS AI</Text>
            {busy && !answer ? (
              <Text style={s.thinking}>{tx(language, "Thinking…")}</Text>
            ) : (
              <Text style={s.answer}>{answer}</Text>
            )}
            {!!error && <Text style={s.error}>{error}</Text>}
          </View>

          <View style={s.actions}>
            <Pressable
              onPress={() => {
                setSelectedId(null);
                setAnswer("");
                setError("");
              }}
              style={s.primary}
            >
              <Text style={s.primaryText}>{tx(language, "All questions →")}</Text>
            </Pressable>

            {!busy && error ? (
              <Pressable
                onPress={() => ask(QUESTIONS.find((item) => item.id === selectedId))}
                style={s.secondary}
              >
                <Text style={s.secondaryText}>{tx(language, "Retry")}</Text>
              </Pressable>
            ) : null}
          </View>

          {!busy && answer ? (
            <View style={s.more}>
              <Text style={s.moreLabel}>{tx(language, "ASK ANOTHER")}</Text>
              <View style={s.moreRow}>
                {suggestions.map((item) => (
                  <Pressable key={item.id} onPress={() => ask(item)} style={s.smallQuestion}>
                    <Text style={s.smallQuestionText}>
                      {item.icon} {tx(language, t(item.q, tier))}
                    </Text>
                  </Pressable>
                ))}
              </View>
            </View>
          ) : null}
        </View>
      )}
    </View>
  );
}

const s = StyleSheet.create({
  container: { width: 1920, height: 1080, backgroundColor: "#070D17" },
  top: {
    height: 72,
    paddingHorizontal: 48,
    flexDirection: "row",
    gap: 26,
    alignItems: "center",
  },
  topText: { color: "#8FA5BC", fontSize: 19, fontWeight: "800" },
  audioButton: {
    marginLeft: "auto",
    borderWidth: 1,
    borderColor: "#1B2A44",
    borderRadius: 999,
    paddingHorizontal: 18,
    paddingVertical: 9,
    backgroundColor: "#0D1626",
  },
  audioButtonOn: { borderColor: "#4FD1FF", backgroundColor: "#0E2237" },
  audioText: { color: "#C7D5E3", fontSize: 17, fontWeight: "800" },
  center: { alignItems: "center", paddingHorizontal: 100 },
  title: { marginTop: 42, color: "#F4F8FC", fontSize: 64, fontWeight: "900" },
  subtitle: {
    marginTop: 10,
    color: "#9FB2C6",
    fontSize: 24,
    lineHeight: 34,
    textAlign: "center",
    maxWidth: 1200,
  },
  grid: {
    marginTop: 42,
    width: 1580,
    flexDirection: "row",
    flexWrap: "wrap",
    gap: 18,
  },
  question: {
    width: 780,
    minHeight: 115,
    borderWidth: 1,
    borderColor: "#1B2A44",
    borderRadius: 999,
    backgroundColor: "#0D1626",
    paddingHorizontal: 34,
    paddingVertical: 24,
    flexDirection: "row",
    alignItems: "center",
    gap: 18,
  },
  icon: { fontSize: 34 },
  questionText: { color: "#F4F8FC", fontSize: 25, fontWeight: "900", flex: 1 },
  foot: { marginTop: 26, color: "#5F7690", fontSize: 16 },
  pressed: { opacity: 0.9, borderColor: "#4FD1FF" },
  answerLayout: { alignItems: "center", paddingHorizontal: 150 },
  answerKicker: {
    marginTop: 32,
    color: "#4FD1FF",
    fontSize: 16,
    fontWeight: "900",
    letterSpacing: 2,
  },
  answerQuestion: {
    marginTop: 10,
    color: "#F4F8FC",
    fontSize: 48,
    fontWeight: "900",
    textAlign: "center",
    maxWidth: 1450,
  },
  answerCard: {
    marginTop: 28,
    width: 1450,
    minHeight: 320,
    maxHeight: 480,
    borderWidth: 1,
    borderColor: "#4FD1FF",
    borderRadius: 22,
    backgroundColor: "#0D1626",
    paddingHorizontal: 42,
    paddingVertical: 34,
  },
  saras: { color: "#4FD1FF", fontSize: 17, fontWeight: "900", letterSpacing: 2 },
  thinking: { marginTop: 24, color: "#9FB2C6", fontSize: 27, fontWeight: "800" },
  answer: { marginTop: 18, color: "#E7EEF6", fontSize: 27, lineHeight: 42 },
  error: { marginTop: 18, color: "#FBBF24", fontSize: 22, lineHeight: 32 },
  actions: { marginTop: 24, flexDirection: "row", gap: 16 },
  primary: {
    borderWidth: 1,
    borderColor: "#4FD1FF",
    borderRadius: 14,
    backgroundColor: "#0E3A50",
    paddingHorizontal: 28,
    paddingVertical: 15,
  },
  primaryText: { color: "#F4F8FC", fontSize: 19, fontWeight: "900" },
  secondary: { paddingHorizontal: 24, paddingVertical: 15 },
  secondaryText: { color: "#8FA5BC", fontSize: 19, fontWeight: "800" },
  more: { marginTop: 24, width: 1450 },
  moreLabel: { color: "#5F7690", fontSize: 15, fontWeight: "900", letterSpacing: 1.7 },
  moreRow: { marginTop: 10, flexDirection: "row", gap: 12 },
  smallQuestion: {
    flex: 1,
    minHeight: 72,
    borderWidth: 1,
    borderColor: "#1B2A44",
    borderRadius: 16,
    backgroundColor: "#0D1626",
    paddingHorizontal: 18,
    paddingVertical: 14,
    justifyContent: "center",
  },
  smallQuestionText: { color: "#C7D5E3", fontSize: 17, fontWeight: "800" },
});
